import { BoxPlot, buildBoxPlotTraces } from './BoxPlot';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import KnowledgeService from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import React from 'react';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';

const knowledgeService = new KnowledgeService();

const messages = defineMessages({
  normalExpressionTCGA: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.normalExpressionTCGA',
    defaultMessage: 'Expression in TCGA Normal Samples',
  },
  tumorExpressionTCGA: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.tumorExpressionTCGA',
    defaultMessage: 'Expression in TCGA Tumor Samples',
  },
  broadTissueInGTEx: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.broadTissueInGTEx',
    defaultMessage: 'Expression in GTEx Normal Broad Tissues',
  },
  specificTissueInGTEx: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.specificTissueInGTEx',
    defaultMessage: 'Expression in GTEx Normal Specific Tissues',
  },
  tumorExpressionInTreeHouse: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.tumorExpressionInTreeHouse',
    defaultMessage: 'Expression in TreeHouse Tumor Samples',
  },
  yaxisTitle: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.yaxisTitle',
    defaultMessage: 'log2(tpm+1)',
  },
  xaxisTitleNormalTCGA: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.xaxisTitleNormalTCGA',
    defaultMessage: 'TCGA Cancer Type (Normal)',
  },
  xaxisTitleTumorTCGA: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.xaxisTitleTumorTCGA',
    defaultMessage: 'TCGA Cancer Type',
  },
  xaxisTitleGTExBroad: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.xaxisTitleGTExBroad',
    defaultMessage: 'GTEx Tissue Type (Broad)',
  },
  xaxisTitleGTExSpecific: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.xaxisTitleGTExSpecific',
    defaultMessage: 'GTEx Tissue Type (Specific)',
  },
  xaxisTitleTumorExpressionInTreeHouse: {
    id: 'omicsExplorer.variantDetails.dialog.visualizations.expressionVisuals.xaxisTitleTumorExpressionInTreeHouse',
    defaultMessage: 'TreeHouse Cancer Type (Pediatric)',
  },
});

const useStyles = makeStyles(theme => ({
  plot: {
    marginBottom: theme.spacing(4),
  },
}));

export const ExpressionVisuals = ({ gene, expression }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const [tcgaNormal, setTcgaNormal] = React.useState(null);
  const [tcgaTumor, setTcgaTumor] = React.useState(null);
  const [gtexBroad, setGtexBroad] = React.useState(null);
  const [gtexSpecific, setGtexSpecific] = React.useState(null);
  const [treeHouse, setTreeHouse] = React.useState(null);
  const [getAggregates, getAggregatesState] = useService(
    knowledgeService.getGeneAggregates,
  );

  React.useEffect(() => {
    getAggregates({ gene });
  }, [gene, getAggregates]);

  React.useEffect(() => {
    if (!getAggregatesState.response) {
      return;
    }

    const { tcgaNormal, tcgaTumor, gtexBroad, gtexSpecific, treeHouse } =
      getAggregatesState.response.data;

    setTcgaNormal(buildBoxPlotTraces(tcgaNormal));
    setTcgaTumor(buildBoxPlotTraces(tcgaTumor));
    setGtexBroad(buildBoxPlotTraces(gtexBroad));
    setGtexSpecific(buildBoxPlotTraces(gtexSpecific));
    setTreeHouse(buildBoxPlotTraces(treeHouse));
  }, [getAggregatesState.response]);

  return (
    <>
      <div className={classes.plot}>
        <Text size={'headline'}>
          {formatMessage(messages.tumorExpressionTCGA)}
        </Text>
        <BoxPlot
          isLoading={getAggregatesState.isFetching}
          data={tcgaTumor}
          ytitle={formatMessage(messages.yaxisTitle)}
          xtitle={formatMessage(messages.xaxisTitleTumorTCGA)}
          expression={expression}
          gene={gene}
        />
      </div>
      <div className={classes.plot}>
        <Text size={'headline'}>
          {formatMessage(messages.normalExpressionTCGA)}
        </Text>
        <BoxPlot
          isLoading={getAggregatesState.isFetching}
          data={tcgaNormal}
          ytitle={formatMessage(messages.yaxisTitle)}
          xtitle={formatMessage(messages.xaxisTitleNormalTCGA)}
          expression={expression}
          gene={gene}
        />
      </div>
      <div className={classes.plot}>
        <Text size={'headline'}>
          {formatMessage(messages.broadTissueInGTEx)}
        </Text>
        <BoxPlot
          isLoading={getAggregatesState.isFetching}
          data={gtexBroad}
          ytitle={formatMessage(messages.yaxisTitle)}
          xtitle={formatMessage(messages.xaxisTitleGTExBroad)}
          expression={expression}
          gene={gene}
        />
      </div>
      <div className={classes.plot}>
        <Text size={'headline'}>
          {formatMessage(messages.specificTissueInGTEx)}
        </Text>
        <BoxPlot
          isLoading={getAggregatesState.isFetching}
          data={gtexSpecific}
          ytitle={formatMessage(messages.yaxisTitle)}
          xtitle={formatMessage(messages.xaxisTitleGTExSpecific)}
          expression={expression}
          gene={gene}
        />
      </div>
      <div>
        <Text size={'headline'}>
          {formatMessage(messages.tumorExpressionInTreeHouse)}
        </Text>
        <BoxPlot
          isLoading={getAggregatesState.isFetching}
          data={treeHouse}
          ytitle={formatMessage(messages.yaxisTitle)}
          xtitle={formatMessage(messages.xaxisTitleTumorExpressionInTreeHouse)}
          expression={expression}
          gene={gene}
        />
      </div>
    </>
  );
};
